import type { Collection, InsertType } from 'dexie';
import { exposeApi } from 'threads-es';
import type {
  ConversationGroupMessageType,
  ConversationMessageType,
  ConversationMessageTypeUnion,
} from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { messageDb as database } from '../../services/database';

type MessageCollection = Collection<
  ConversationMessageTypeUnion,
  number,
  InsertType<ConversationMessageTypeUnion, 'ID'>
>;

const isBurned = (
  message: Pick<ConversationMessageTypeUnion, 'MsgType' | 'IsRead' | 'ReadDelete' | 'AutoDeleteTime' | 'ReadStamp'>,
) =>
  Boolean(
    message.ReadDelete &&
      message.MsgType !== 'notice' &&
      message.IsRead &&
      message.ReadStamp &&
      message.AutoDeleteTime &&
      message.ReadStamp + message.AutoDeleteTime * 1000 <= Date.now(),
  );

const messageWorkerAPI = {
  getMessages: async (
    type: ConversationType,
    id: number,
    limit = 50,
    direction: 'prev' | 'next' = 'prev',
    anchorMessageId = Number.MAX_SAFE_INTEGER,
  ) => {
    const messageCollection: MessageCollection =
      type === 'session'
        ? database.sessionMessages.where({ ChatSessionID: id })
        : database.groupMessages.where({ GroupID: id });

    if (anchorMessageId >= Number.MAX_SAFE_INTEGER) {
      return messageCollection.reverse().limit(limit).toArray();
    }
    if (anchorMessageId !== 0) {
      const start = messageCollection
        .reverse()
        .and((m) => m.ID === anchorMessageId)
        .limit(1);
      if (start)
        return messageCollection
          .and((m) => m.ID < anchorMessageId)
          .reverse()
          .limit(limit)
          .toArray();
    }

    if (direction === 'prev')
      return messageCollection
        .and((m) => m.ID < anchorMessageId)
        .reverse()
        .limit(limit)
        .toArray();

    return messageCollection
      .and((m) => m.ID > anchorMessageId)
      .limit(limit)
      .toArray();
  },
  getLastReadMessageId: async (type: ConversationType, id: number): Promise<number> => {
    let collection: MessageCollection;
    if (type === 'group') {
      collection = database.groupMessages.where({ GroupID: id });
    } else {
      collection = database.sessionMessages.where({ ChatSessionID: id });
    }

    return collection
      .filter((m) => m.Status !== null)
      .reverse()
      .limit(1)
      .primaryKeys()
      .then((keys) => keys.at(0) || 0);
  },
  addMessage: async (message: ConversationMessageTypeUnion) => {
    if (!shouldPersistMessage(message)) return;

    const isGroup = 'GroupID' in message;

    await database.transaction('rw', isGroup ? database.groupMessages : database.sessionMessages, async (tx) => {
      if (isGroup) {
        await tx.groupMessages.delete(message.TimeStamp);
        await tx.groupMessages.put(message);
      } else {
        await tx.sessionMessages.delete(message.TimeStamp);
        await tx.sessionMessages.put(message);
      }
    });
  },
  addMessages: async (messages: ConversationMessageTypeUnion[]) => {
    const newSessionMessages = messages.filter(isSessionMessage).filter(shouldPersistMessage);
    const newGroupMessages = messages.filter(isGroupMessage).filter(shouldPersistMessage);

    return database.transaction('rw', [database.groupMessages, database.sessionMessages], async (tx) => {
      // session messages
      const existedSessionMessages = tx.sessionMessages.where('ID').anyOf(newSessionMessages.map((m) => m.ID));

      if ((await existedSessionMessages.count()) > 0) {
        await existedSessionMessages.modify((m) => {
          const newMessage = newSessionMessages.find((nm) => nm.ID === m.ID);

          m.Status = newMessage?.Status ?? m.Status;
          m.ReadDelete = newMessage?.ReadDelete ?? m.ReadDelete;
          m.IsRead = newMessage?.IsRead || m.IsRead;
          m.ReadStamp = newMessage?.ReadStamp || m.ReadStamp;
          m.Message = newMessage?.Message || m.Message;
        });
      }

      await tx.sessionMessages.bulkPut(newSessionMessages);

      // group messages
      const existedGroupMessages = tx.groupMessages.where('ID').anyOf(newGroupMessages.map((m) => m.ID));

      if ((await existedGroupMessages.count()) > 0) {
        await existedGroupMessages.modify((m) => {
          const newMessage = newGroupMessages.find((nm) => nm.ID === m.ID);

          m.Status = newMessage?.Status ?? m.Status;
          m.ReadDelete = newMessage?.ReadDelete ?? m.ReadDelete;
          m.IsRead = newMessage?.IsRead || m.IsRead;
          m.ReadStamp = newMessage?.ReadStamp || m.ReadStamp;
          m.Message = newMessage?.Message || m.Message;
        });
      }

      await tx.groupMessages.bulkPut(newGroupMessages);
    });
  },
  validateMessage: async (
    type: ConversationType,
    validateId: number,
    message: ConversationMessageTypeUnion,
  ): Promise<ConversationMessageTypeUnion | undefined> =>
    database.transaction('rw', type === 'group' ? database.groupMessages : database.sessionMessages, async (tx) => {
      if (type === 'group') {
        await tx.groupMessages.where('ID').equals(validateId).delete();
        await tx.groupMessages.put(message);
      } else {
        await tx.sessionMessages.where('ID').equals(validateId).delete();
        await tx.sessionMessages.put(message);
      }

      return message;
    }),
  deleteMessage: async (
    type: ConversationType,
    messageId: number,
  ): Promise<ConversationMessageTypeUnion | undefined> => {
    let message: MessageCollection;

    if (type === 'group') {
      message = database.groupMessages.where('ID').equals(messageId).limit(1);
    } else {
      message = database.sessionMessages.where('ID').equals(messageId).limit(1);
    }

    message.modify((m) => {
      m.Status = false;
      m.ReadDelete = false;
    });

    return message.first();
  },
  deleteMessages: async (type: ConversationType, messageIds: number[]): Promise<ConversationMessageTypeUnion[]> =>
    database.transaction('rw', type === 'group' ? database.groupMessages : database.sessionMessages, async (tx) => {
      let collection: MessageCollection;
      if (type === 'group') {
        collection = tx.groupMessages
          .where('ID')
          .anyOf(messageIds)
          .filter((m) => !isBurned(m));
      } else {
        collection = tx.sessionMessages
          .where('ID')
          .anyOf(messageIds)
          .filter((m) => !isBurned(m));
      }

      await collection.modify((m) => {
        m.Status = false;
        m.ReadDelete = false;
      });

      return collection.toArray();
    }),
  deleteMessagesBySession: async (type: ConversationType, id: number): Promise<ConversationMessageTypeUnion[]> => {
    let collection: MessageCollection;
    if (type === 'group') {
      collection = database.groupMessages.where({ GroupID: id });
    } else {
      collection = database.sessionMessages.where({ ChatSessionID: id });
    }

    await collection.modify((m) => {
      m.Status = false;
      m.ReadDelete = false;
    });

    return collection.toArray();
  },
  deleteMessagesByUser: async (
    userId: number,
    type?: ConversationType,
    id?: number,
  ): Promise<ConversationMessageTypeUnion[]> => {
    if (type && id) {
      let collection: MessageCollection;
      if (type === 'group') {
        collection = database.groupMessages.where({ GroupID: id, UserID: userId });
      } else {
        collection = database.sessionMessages.where({ ChatSessionID: id, UserID: userId });
      }
      await collection.modify((m) => {
        m.Status = false;
        m.ReadDelete = false;
      });

      return collection.toArray();
    }
    return database.transaction('rw', [database.groupMessages, database.sessionMessages], async (tx) => {
      const messagesFromSessions = tx.sessionMessages.where({ UserID: userId });
      const messagesFromGroups = tx.groupMessages.where({ UserID: userId });

      await messagesFromSessions.modify((m) => {
        m.Status = false;
        m.ReadDelete = false;
      });
      await messagesFromGroups.modify((m) => {
        m.Status = false;
        m.ReadDelete = false;
      });

      return [...(await messagesFromSessions.toArray()), ...(await messagesFromGroups.toArray())];
    });
  },
  markMessageRead: async (
    type: ConversationType,
    messageId: number,
    userId?: number,
    readStamp = Date.now(),
    fromCounterpart = true,
  ): Promise<ConversationMessageTypeUnion | undefined> =>
    database.transaction('rw', type === 'group' ? database.groupMessages : database.sessionMessages, async (tx) => {
      const messageCollection =
        type === 'group' ? tx.groupMessages.where({ ID: messageId }) : tx.sessionMessages.where({ ID: messageId });
      const message = await messageCollection.first();

      if (message) {
        if (
          message.ReadDelete &&
          message.MsgType !== 'notice' &&
          (fromCounterpart ? message.UserID === userId : message.UserID !== userId)
        ) {
          await messageCollection.modify((m) => {
            m.IsRead = true;
            m.ReadStamp ||= readStamp;
          });

          message.IsRead = true;
          message.ReadStamp = readStamp;

          return message;
        }
      }
    }),
  markMessagesReadBySession: async (
    type: ConversationType,
    id: number,
    userId?: number,
    readStamp = Date.now(),
    fromCounterpart = true,
  ) => {
    let collection: MessageCollection;
    if (type === 'group') {
      collection = database.groupMessages.where({ GroupID: id });
    } else {
      collection = database.sessionMessages.where({ ChatSessionID: id });
    }

    const markedMessages: ConversationMessageTypeUnion[] = [];
    await collection.modify((m) => {
      if (m.ReadDelete && m.MsgType !== 'notice' && (fromCounterpart ? m.UserID === userId : m.UserID !== userId)) {
        m.IsRead = true;
        m.ReadStamp ||= readStamp;

        markedMessages.push(m);
      }
    });
    return markedMessages;
  },
  hideMessages: async (type: ConversationType, id: number) => {
    let collection: MessageCollection;

    if (type === 'group') {
      collection = database.groupMessages.where({ GroupID: id }).filter((m) => m.Status !== null);
    } else {
      collection = database.sessionMessages.where({ ChatSessionID: id }).filter((m) => m.Status !== null);
    }

    await collection.modify((m) => {
      m.Status = false;
      m.ReadDelete = false;
    });

    return collection.primaryKeys();
  },
  unmarkMessageStale: async (message: ConversationMessageTypeUnion, oldMessageId: number) => {
    const isGroup = 'GroupID' in message;

    return database.transaction('rw', isGroup ? database.groupMessages : database.sessionMessages, async (tx) => {
      if (isGroup) {
        await tx.groupMessages.delete(oldMessageId);
        await tx.groupMessages.put(message);
      } else {
        await tx.sessionMessages.delete(oldMessageId);
        await tx.sessionMessages.put(message);
      }
    });
  },
  trimMessages: async (retentionTime: number) =>
    database.transaction('rw', [database.groupMessages, database.sessionMessages], async (tx) => {
      const thresholdTime = Date.now() - retentionTime;

      await tx.groupMessages.where('RetrieveTime').below(thresholdTime).delete();
      await tx.sessionMessages.where('RetrieveTime').below(thresholdTime).delete();
    }),
  putMessages: async (messages: ConversationMessageTypeUnion[]) => {
    const newSessionMessages = messages.filter(isSessionMessage).filter(shouldPersistMessage);
    const newGroupMessages = messages.filter(isGroupMessage).filter(shouldPersistMessage);

    return database.transaction('rw', [database.groupMessages, database.sessionMessages], async (tx) => {
      if (newSessionMessages.length > 0) {
        await tx.sessionMessages.bulkPut(newSessionMessages);
      }
      if (newGroupMessages.length > 0) {
        await tx.groupMessages.bulkPut(newGroupMessages);
      }
    });
  },
  clearAllMessages: async () =>
    database.transaction('rw', [database.groupMessages, database.sessionMessages], async (tx) => {
      await tx.groupMessages.clear();
      await tx.sessionMessages.clear();
    }),
  clearMessagesBySession: async (type: ConversationType, id: number) => {
    let collection: MessageCollection;
    if (type === 'group') {
      collection = database.groupMessages.where({ GroupID: id });
    } else {
      collection = database.sessionMessages.where({ ChatSessionID: id });
    }

    return collection.delete();
  },
};

exposeApi(messageWorkerAPI);

export type MessageWorkerAPI = typeof messageWorkerAPI;

function isSessionMessage(message: ConversationMessageTypeUnion): message is ConversationMessageType {
  return 'ChatSessionID' in message && message.ChatSessionID > 0;
}

function isGroupMessage(message: ConversationMessageTypeUnion): message is ConversationGroupMessageType {
  return 'GroupID' in message && message.GroupID > 0;
}

function shouldPersistMessage(message: Pick<ConversationMessageTypeUnion, 'CreateTime' | 'RetrieveTime'>): boolean {
  const threshold = Date.now() - 45 * 24 * 60 * 60 * 1000;
  return Math.min(message.CreateTime, message.RetrieveTime) >= threshold;
}
